import { useCallback, useEffect, useMemo, useState } from "react";
import { enqueueSnackbar } from "notistack";
import { NotificationContext } from "./notificationContext";
import {
  useGetNotificationsMutation,
  useMarkNotificationAsReadMutation,
  useMarkAllNotificationsAsReadMutation,
  useDeleteNotificationMutation,
} from "../../services/notification-query";
import { fetchEventSource } from "@microsoft/fetch-event-source";
import { useAuthContext } from "src/auth/hooks";
import useFirebaseApp from "src/hooks/use-firebase-app";

const NotificationProvider = ({ children }) => {
  const { user } = useAuthContext();

  const { firebaseAuth } = useFirebaseApp();

  const [notificationMeta, setNotificationMeta] = useState({
    total: 0,
    page: 1,
    size: 10,
    totalPages: 0,
  });

  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoadingNotifications, setIsLoadingNotifications] = useState(false);

  const [getNotifications] = useGetNotificationsMutation();
  const [markNotificationAsRead] = useMarkNotificationAsReadMutation();
  const [markAllNotificationsAsRead] = useMarkAllNotificationsAsReadMutation();
  const [deleteNotification] = useDeleteNotificationMutation();

  const fetchNotifications = useCallback(async ({ page = 1, size = 10, isLoadMore = false } = {}) => {
    try {
      if (!isLoadMore) setIsLoadingNotifications(true);

      const response = await getNotifications({ page, size }).unwrap();
      const list = Array.isArray(response?.data?.content) ? response.data.content : [];
      const meta = response?.data?.meta || { total: 0, page: 1, size, totalPages: 0 };

      setNotifications(prev => isLoadMore ? [...prev, ...list] : list);
      
      setNotificationMeta(meta);
    } catch (error) {
      enqueueSnackbar(error?.data?.error?.message || "Failed to load notifications", { variant: "error" });
    } finally {
      setIsLoadingNotifications(false);
    }
  }, [getNotifications]);

  useEffect(() => {
    if (!user) return;

    fetchNotifications();
  }, [user, fetchNotifications]);

  //listen for SSE events for notifications
  useEffect(() => {
    if (!user) return;

    let isCancelled = false;
    const controller = new AbortController();

    const connectSSE = async () => {
      try {
        const token = sessionStorage.getItem('accessToken');

        if (!token) {
          console.log("No token found for SSE!");
          return;
        }

        await fetchEventSource(
          `${process.env.REACT_APP_HOST_API}/api/v1/notifications/stream`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
            signal: controller.signal,
            openWhenHidden: true,

            onopen(response) {
              console.log("Notification SSE connected:", response.status);
            },

            onmessage(event) {
              console.log("Notification SSE event received:", event.event, event.data);

              const data = JSON.parse(event.data);

              if (event.event === "new_notification") {
                setUnreadCount(data.unreadCount);

                setNotifications((prev) => {
                  const alreadyExists = prev.some((item) => item.id === data.notification.id);
                  return alreadyExists ? prev : [data.notification, ...prev];
                });

                return;
              }

              if (event.event === "unread_count") {
                setUnreadCount(data.unreadCount);
              }
            },

            onerror(error) {
              console.log("Notification SSE error:", error);
            },
          }
        );
      } catch (error) {
        if (!isCancelled) {
          console.log("Notification SSE connection failed:", error);
        }
      }
    };

    connectSSE();

    return () => {
      isCancelled = true;
      controller.abort();
    };
  }, [user]);

  const markAsRead = useCallback(async (id) => {
    try {
      const response = await markNotificationAsRead({ id }).unwrap();

      const updatedNotification = response?.data || response;

      setNotifications((prev) =>
        prev.map((item) =>
          item.id === id
            ? {
                ...item,
                isRead: true,
                readAt: updatedNotification?.readAt || new Date().toISOString(),
              }
            : item
        )
      );

      setUnreadCount((prev) => Math.max(prev - 1, 0));
    } catch (error) {
      enqueueSnackbar(
        error?.data?.error?.message || "Failed to mark notification as read",
        { variant: "error" }
      );
    }
  }, [markNotificationAsRead]);

  const markAllAsRead = useCallback(async () => {
    try {
      await markAllNotificationsAsRead().unwrap();

      setNotifications((prev) =>
        prev.map((item) => ({
          ...item,
          isRead: true,
          readAt: item.readAt || new Date().toISOString(),
        }))
      );

      setUnreadCount(0);
    } catch (error) {
      enqueueSnackbar(
        error?.data?.error?.message || "Failed to mark all notifications as read",
        { variant: "error" }
      );
    }
  }, [markAllNotificationsAsRead]);

  const deleteNotificationById = useCallback(async (id) => {
    try {
      await deleteNotification({ id }).unwrap();

      setNotifications((prev) => {
        const deletedItem = prev.find((item) => item.id === id);

        if (deletedItem && !deletedItem.isRead) {
          setUnreadCount((count) => Math.max(count - 1, 0));
        }

        return prev.filter((item) => item.id !== id);
      });
    } catch (error) {
      enqueueSnackbar(
        error?.data?.error?.message || "Failed to delete notification",
        { variant: "error" }
      );
    }
  }, [deleteNotification]);

  const value = useMemo(() => ({
    notifications,
    notificationMeta,
    unreadCount,
    isLoadingNotifications,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    deleteNotificationById,
  }), [
    notifications,
    notificationMeta,
    unreadCount,
    isLoadingNotifications,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    deleteNotificationById,
  ]);

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export default NotificationProvider;